import React, { FC, useState } from 'react'
import classNames from 'classnames'
import { CountryCode, E164Number } from 'libphonenumber-js'
import { formatPhoneNumber } from 'react-phone-number-input'
import { Button } from '@/atoms/Button'
import { CircleAlertIcon } from '@/atoms/Icons/CircleAlertIcon'
import { IntlPhoneInput } from '@/atoms/Input/IntlPhoneInput/IntlPhoneInput'
import { CaptionSM, Text } from '@/atoms/Text'
import { useToast } from '@/molecules/Toast/ToastProvider'
import { useUser } from '@/services/UserService'
import { useTranslate } from '@/utils/translate/translate-client'
import { useTheaterPreferences } from '../TheaterPreferencesContext'
import { DayTimePreferencesList } from '../components/DayTimePreferencesList'
import { TheaterCard } from '../components/TheaterCard'
import { DAYS_OF_WEEK } from '../constants'

interface DayButtonProps {
  day: string
  isSelected: boolean
  onClick?: () => void
}

const DayButton: FC<DayButtonProps> = ({ day, isSelected, onClick }) => (
  <button
    onClick={onClick}
    className={classNames(
      'rounded-full px-4 py-1.5 text-xs font-normal transition-colors',
      isSelected ? 'bg-black text-white' : 'bg-white text-black border border-black',
    )}
  >
    {day}
  </button>
)

interface ExistingPreferencesStepProps {
  isAutobookFlow?: boolean
  onEditTheater: () => void
  onClose: () => void
  onSave?: () => void
}

export const ExistingPreferencesStep: FC<ExistingPreferencesStepProps> = ({
  onEditTheater,
  onClose,
  onSave,
  isAutobookFlow,
}) => {
  const { t } = useTranslate('account')
  const [isEditingPhone, setIsEditingPhone] = useState(false)
  const [invalidMessage, setInvalidMessage] = useState('')
  const [isValid, setIsValid] = useState(false)
  const { showToast } = useToast()

  const user = useUser()

  const { phoneNumber, selectedTheater, dayTimePreferences, loading, setPhoneNumber, handleSave, handleDayTimeToggle } =
    useTheaterPreferences()

  const handleEditPhone = () => {
    setIsEditingPhone(true)
  }

  const handlePhoneChange = (value?: E164Number) => {
    setPhoneNumber(value)
    setInvalidMessage('')
    setIsValid(true)
  }

  const handleSavePhone = () => {
    setIsEditingPhone(false)
    if (!isValid) {
      setInvalidMessage(t('invalidNumber', 'Invalid number'))
      return
    }
    handleSave()
  }

  const handleSavePreferences = () => {
    handleSave()
    if (isAutobookFlow) {
      onSave?.()
    } else {
      onClose()
      showToast(t('preferencesUpdated', 'Theater preferences updated successfully'))
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-col gap-4 p-6">
          <div className="mr-12 flex flex-col gap-4">
            <Text weight="bold" className="text-xl">
              {isAutobookFlow
                ? t('openingWeekShowtimes', 'Opening Week Showtimes')
                : t('yourTheatricalPreferences', 'Your Theatrical Preferences')}
            </Text>
            <Text weight="normal" color="gray-600" className="text-xs leading-6">
              {isAutobookFlow
                ? t(
                    'chooseYourPreferredDaysAndTimes',
                    'Choose your preferred days and times to watch movies during opening week.',
                  )
                : t(
                    'setYourPreferredLocationDaysAndTimes',
                    'Set your preferred location, days and times to get personalized movie recommendations and showtimes.',
                  )}
            </Text>
          </div>
          {/* Phone Number Section */}
          {phoneNumber && !isAutobookFlow && (
            <div className="flex flex-col gap-1">
              <div className="mb-2 flex items-center justify-between">
                <Text weight="normal" color="gray-600" className="text-xs leading-6">
                  {t('phoneNumber', 'Phone Number')}
                </Text>
                <Button
                  variant="lightgray"
                  onClick={isEditingPhone ? handleSavePhone : handleEditPhone}
                  className="px-3 py-1 text-xs"
                  disabled={loading}
                >
                  {isEditingPhone ? t('save', 'Save') : t('edit', 'Edit')}
                </Button>
              </div>
              {isEditingPhone ? (
                <>
                  <IntlPhoneInput
                    countryCode={(user.userLocation?.countryCode as CountryCode) || 'US'}
                    onPhoneValue={handlePhoneChange}
                    onValidPhone={setIsValid}
                    inputClassName={classNames(
                      'photon-caption-md p-4 pl-0 border border-r-0 border-y-0 border-transparent bg-core-gray-100',
                      'focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent',
                      {
                        'border-l-error-500 bg-error-100': invalidMessage,
                        'focus:bg-core-gray-100': !invalidMessage,
                      },
                    )}
                    placeholder={t('mobilePhoneNumber', 'Mobile phone number')}
                    wrapperClassName={classNames(
                      'w-full rounded-lg border-[1.5px] bg-core-gray-100 overflow-hidden text-core-gray-950',
                      {
                        'border-error-500 bg-error-100': invalidMessage,
                        'border-core-gray-300 focus-within:border-core-gray-300 focus-within:bg-core-gray-100':
                          !invalidMessage,
                      },
                    )}
                  />
                  {invalidMessage && (
                    <CaptionSM color="warm-01" className="mt-1 flex gap-1">
                      <CircleAlertIcon size={16} color="warm-01" /> {invalidMessage}
                    </CaptionSM>
                  )}
                </>
              ) : (
                <Text weight="semibold" className="text-sm">
                  {phoneNumber ? formatPhoneNumber(phoneNumber) : ''}
                </Text>
              )}
            </div>
          )}

          {/* Theater Preference Section */}
          <div className="rounded-md">
            <div className="mb-2 flex items-center justify-between">
              <Text weight="normal" className="text-xs" color="gray-600">
                {t('preferredLocation', 'Preferred Location')}
              </Text>
              <Button variant="lightgray" onClick={onEditTheater} className="px-3 py-1 text-xs">
                {t('edit', 'Edit')}
              </Button>
            </div>
            {selectedTheater && <TheaterCard theater={selectedTheater} />}
          </div>

          {/* Preferred Days Section */}
          <div className="rounded-md">
            <div className="mb-2 flex items-center justify-between">
              <Text weight="normal" className="text-xs" color="gray-600">
                {t('preferredOpeningWeekDays', 'Preferred Opening Week Days')}
              </Text>
            </div>
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK.map((day) => (
                <DayButton
                  key={day.id}
                  day={day.label}
                  isSelected={day.id in dayTimePreferences}
                  onClick={() => {
                    if (day.id in dayTimePreferences) {
                      handleDayTimeToggle(day.id, '')
                    } else {
                      handleDayTimeToggle(day.id, '')
                    }
                  }}
                />
              ))}
            </div>
          </div>

          {/* Day Time Selection Section */}
          <div className="rounded-md">
            <div className="mb-2 flex items-center justify-between">
              <Text weight="normal" className="text-xs" color="gray-600">
                {t('preferredTimes', 'Preferred Times')}
              </Text>
            </div>
            <DayTimePreferencesList dayTimePreferences={dayTimePreferences} />
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="shrink-0 border-t border-gray-200 bg-white p-6">
        <div className="flex gap-2">
          <Button variant="lightgray" onClick={onClose} className="flex-1 py-3 text-sm">
            {t('cancel', 'Cancel')}
          </Button>
          <Button variant="black" onClick={handleSavePreferences} className="flex-1 py-3 text-sm" disabled={loading}>
            {loading ? t('saving', 'Saving...') : t('save', 'Save')}
          </Button>
        </div>
      </div>
    </div>
  )
}
